/**
 * El hero de la landing como componente de acción (ADR 153).
 *
 * Su único trabajo extra sobre el `<form>` que ya funciona sin JavaScript: notar
 * cuándo lo que el usuario trae no es una carta sino una lista, y ofrecerle
 * cotizarla completa. El autocompletar lo sigue haciendo `predictive-search.js`,
 * intacto.
 *
 * Por qué la detección vive en el `paste` y no en el `input`: un `<input>` de una
 * línea se come los saltos de línea al pegar, así que para cuando el valor
 * cambia ya no hay forma de saber que venían varias cartas. El portapapeles sí
 * las trae.
 */
class VaultHeroBuscador extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.vault-buscador__input');
    this.zonaLista = this.querySelector('[data-vault-lista]');
    this.textarea = this.querySelector('[data-vault-textarea]');
    this.conteo = this.querySelector('[data-vault-conteo]');
    this.plural = this.querySelector('[data-vault-plural]');
    this.botonCotizar = this.querySelector('[data-vault-cotizar]');
    this.botonCancelar = this.querySelector('[data-vault-cancelar]');
    this.destino = this.dataset.destinoLista || '/pages/deck-builder';

    if (!this.input || !this.zonaLista) return;

    this.input.addEventListener('paste', this.alPegar.bind(this));
    this.textarea.addEventListener('input', this.alEscribirLista.bind(this));
    this.botonCancelar.addEventListener('click', this.volverAUnaCarta.bind(this));
    this.botonCotizar.addEventListener('click', this.cotizar.bind(this));

    const formulario = this.querySelector('form');
    if (formulario) {
      formulario.addEventListener('submit', () => {
        if (window.vaultEvento) window.vaultEvento('landing_hero_search');
      });
    }

    // Los botones son hermanos del buscador, no hijos: viven en el hero, no dentro
    // del componente. Buscarlos aquí adentro devolvía cero y el evento no existía.
    const hero = this.closest('.vault-hero') || document;
    hero.querySelectorAll('[data-vault-cta]').forEach((cta) => {
      cta.addEventListener('click', () => {
        if (window.vaultEvento) window.vaultEvento('landing_hero_cta', cta.dataset.vaultCta);
      });
    });

    /*
     * El chip que abre el modo lista sin salir del hero. Es el único acceso que
     * suma a la métrica de esta landing en vez de restarle: los demás se llevan
     * al visitante a otra página, este lo deja aquí y le enseña la
     * multibúsqueda, que es justo lo que el ADR 153 quiere medir.
     */
    hero.querySelectorAll('[data-vault-abrir-lista]').forEach((chip) => {
      chip.addEventListener('click', () => {
        this.mostrarLista();
        if (window.vaultEvento) window.vaultEvento('landing_list_detected', '0');
      });
    });

    this.vigilarElAnclaje();
    this.flotarLosPaneles();
  }

  /**
   * Los desplegables del buscador flotan; no empujan.
   *
   * Son dos y son hermanos: el de Smart Search —las instrucciones de teclado y
   * las categorías de filtro— y el del autocompletar. En la barra anclada vivían
   * dentro de un contenedor que ya flotaba sobre la página, así que daba igual
   * que ocuparan sitio. Aquí van dentro del hero: al abrirse empujaban los
   * botones 200px hacia abajo y la pantalla entera daba un salto al enfocar.
   *
   * Se meten los dos en un contenedor absoluto para que caigan sobre el
   * contenido en vez de moverlo, y para que sigan apilados en su orden — que es
   * lo que no se puede lograr sacándolos por separado.
   */
  flotarLosPaneles() {
    const formulario = this.querySelector('form');
    if (!formulario) return;

    const acomodar = () => {
      const sugerencias = formulario.querySelector(':scope > .smart-search__suggestions');
      const autocompletar = formulario.querySelector(':scope > .predictive-search');
      if (!sugerencias && !autocompletar) return false;

      let paneles = formulario.querySelector('.vault-buscador__paneles');
      if (!paneles) {
        paneles = document.createElement('div');
        paneles.className = 'vault-buscador__paneles';
        formulario.appendChild(paneles);
      }
      // Se mueven, no se clonan: Smart Search y `predictive-search` guardan la
      // referencia al nodo, y una copia los dejaría hablándole a un huérfano.
      if (sugerencias) paneles.appendChild(sugerencias);
      if (autocompletar) paneles.appendChild(autocompletar);
      return Boolean(sugerencias && autocompletar);
    };

    // Smart Search crea su panel al arrancar, después que este componente: se
    // intenta al cargar y, si todavía no estaba, en cuanto el campo se usa.
    if (!acomodar()) {
      window.addEventListener('load', acomodar, { once: true });
      this.addEventListener('focusin', acomodar, { once: true });
    }
  }

  /**
   * Al bajar, el campo se vuelve la barra del top.
   *
   * Se ancla el mismo elemento en vez de pintar una copia en el header: dos
   * buscadores en una página son dos ids repetidos, dos autocompletares y dos
   * estados que se contradicen. Así, lo que escribiste y los chips que pusiste
   * siguen ahí cuando la barra aparece.
   *
   * El disparador es la posición del campo dentro del documento, guardada una
   * vez. No se usa un marcador en el DOM —un div de alto cero no genera caja y
   * su posición se lee siempre como 0— ni el rect del propio campo, que una vez
   * anclado devuelve la del header y ya nunca se soltaría.
   */
  vigilarElAnclaje() {
    const revisar = this.revisarAnclaje.bind(this);
    window.addEventListener('scroll', revisar, { passive: true });
    window.addEventListener('resize', () => {
      // Al cambiar el ancho, el hero cambia de alto: la posición guardada deja de
      // valer y hay que volver a medirla con el campo en su sitio.
      this.desanclar();
      this.posicionOriginal = null;
      revisar();
    }, { passive: true });

    // La primera medida NO va en `connectedCallback`: ahí el navegador aún no
    // hizo el layout, se lee un 0 y el campo nace anclado en lo alto de la página.
    if (document.readyState === 'complete') setTimeout(revisar, 0);
    else window.addEventListener('load', revisar, { once: true });
  }

  /**
   * Dónde TERMINA el header, no cuánto mide: el `.section-header` no empieza en
   * el borde de la pantalla, así que usar su alto dejaba la barra 8px por debajo
   * y se le montaba encima. Se mide cada vez porque el header se encoge al bajar
   * (`reduce-logo-size`).
   */
  altoDelHeader() {
    const header = document.querySelector('.section-header') || document.querySelector('header');
    if (!header) return 65;
    return Math.max(0, header.getBoundingClientRect().bottom);
  }

  revisarAnclaje() {
    // Con la lista abierta no se ancla: una lista de 40 cartas no cabe en una
    // barra, y a quien la está escribiendo no se le mueve el campo.
    if (this.classList.contains('is-lista')) return this.desanclar();

    const alto = this.altoDelHeader();
    this.style.setProperty('--vault-hero-anclaje', `${alto}px`);

    if (this.posicionOriginal == null) {
      const arriba = this.getBoundingClientRect().top + window.scrollY;
      // Un 0 aquí es "todavía no hay layout", no "el campo está hasta arriba":
      // el hero siempre empieza debajo del header.
      if (arriba <= 0) return;
      this.posicionOriginal = arriba;
    }

    if (window.scrollY + alto > this.posicionOriginal) this.anclar();
    else this.desanclar();
  }

  anclar() {
    if (this.classList.contains('is-anclado')) return;
    // El hueco se reserva antes de sacar el campo del flujo: si no, la página
    // pega un salto del alto del campo justo cuando el usuario está bajando.
    this.style.setProperty('--vault-hero-hueco', `${this.offsetHeight}px`);
    this.classList.add('is-anclado');
  }

  desanclar() {
    if (!this.classList.contains('is-anclado')) return;
    this.classList.remove('is-anclado');
    this.style.removeProperty('--vault-hero-hueco');
  }

  /** Una lista es texto con al menos dos líneas que parecen carta. */
  static lineasDeCarta(texto) {
    return String(texto || '')
      .split(/\r?\n/)
      .map((linea) => linea.trim())
      .filter((linea) => linea.length > 1);
  }

  /** Copias pedidas: "4 Sol Ring" son cuatro; una línea sin número, una. */
  static copias(texto) {
    return VaultHeroBuscador.lineasDeCarta(texto).reduce((suma, linea) => {
      const cantidad = linea.replace(/^SB:\s*/i, '').match(/^(\d+)\s*x?\s+/i);
      return suma + (cantidad ? parseInt(cantidad[1], 10) : 1);
    }, 0);
  }

  static esLista(texto) {
    return VaultHeroBuscador.lineasDeCarta(texto).length >= 2;
  }

  alPegar(evento) {
    const pegado = (evento.clipboardData || window.clipboardData)?.getData('text') || '';
    if (!VaultHeroBuscador.esLista(pegado)) return;

    // Se toma el control del pegado: si se deja pasar, el input aplana la lista
    // en una sola línea y el usuario ve un engendro en vez de sus cartas.
    evento.preventDefault();
    this.textarea.value = pegado.trim();
    this.mostrarLista();
    this.actualizarConteo();

    if (window.vaultEvento) {
      window.vaultEvento('landing_list_detected', String(this.cartas()));
    }
  }

  cartas() {
    return VaultHeroBuscador.lineasDeCarta(this.textarea.value).length;
  }

  actualizarConteo() {
    const total = this.cartas();
    this.conteo.textContent = String(total);
    this.plural.textContent = total === 1 ? 'carta' : 'cartas';
    this.botonCotizar.hidden = total === 0;
  }

  alEscribirLista() {
    this.actualizarConteo();
  }

  mostrarLista() {
    this.desanclar();
    this.classList.add('is-lista');
    this.zonaLista.hidden = false;
    this.textarea.focus();
  }

  volverAUnaCarta() {
    this.classList.remove('is-lista');
    this.zonaLista.hidden = true;
    this.textarea.value = '';
    this.input.value = '';
    this.input.focus();
  }

  /**
   * La lista viaja al Buscador de Decks por la URL. Ahí ya vive el matching
   * contra el catálogo: no se reimplementa aquí.
   */
  cotizar(evento) {
    evento.preventDefault();
    const lista = this.textarea.value.trim();
    if (!lista) return;

    // `target` sigue siendo el número de líneas (la serie de siempre). La
    // metadata lleva las copias pedidas: aquí todavía no se sabe qué falta —eso
    // lo resuelve el catálogo en el Buscador de Decks—, así que los faltantes
    // llegan en el `deck_search` con `origen: 'url'` que dispara la página
    // siguiente. Mandar `faltantes: 0` desde aquí sería afirmar algo que no
    // sabemos.
    if (window.vaultEvento) {
      window.vaultEvento('landing_list_cotizar', String(this.cartas()), {
        pedidas: VaultHeroBuscador.copias(lista)
      });
    }

    const url = new URL(this.destino, window.location.origin);
    url.searchParams.set('lista', lista);
    window.location.href = url.toString();
  }
}

customElements.define('vault-hero-buscador', VaultHeroBuscador);
